import { InteractionRequiredAuthError, RedirectRequest } from "@azure/msal-browser";
import axios, { AxiosHeaders, AxiosRequestConfig, AxiosRequestHeaders, AxiosResponse } from "axios";
import { getAPIMKey, getApplicationId, getFcltAppUrl, getMSALBaseUrl, getSigninPolicy, getSignupPolicy, getUserApiUrl } from "./config";
import { getMsalInstance, loginRequest } from "../authConfig";
import Swal, { SweetAlertResult } from 'sweetalert2'
// Define the type for the API request options
export interface ApiRequestParams extends AxiosRequestConfig {
    endpoint: string;
    method?: "GET" | "POST" | "PUT" | "DELETE";
    payload?: any;
    baseUrl?: string;
    body?: any;
    headers?: AxiosRequestHeaders;
    keepAlive?: true | false;
}

export interface InviteByBidders {
    endpoint: string;
    method?: "GET" | "POST" | "PUT" | "DELETE";
    payload: any
}
// In-memory cache for the access token and its expiration time
let cachedToken: string | null = null;
let swalInstance: SweetAlertResult | null = null
let interactionInProgress = false; // Global variable to track interaction state
export const fetchToken = async () => {
    let instance = getMsalInstance();
    await instance.initialize(); // Add this line
    if (!instance) return
    if (interactionInProgress) {
        console.log("Interaction already in-progress", interactionInProgress)
        return
    }
    // If a cached token exists and is still valid, return it
    if (cachedToken && !isTokenExpired(cachedToken)) {
        return cachedToken;
    }
    const accounts = instance.getAllAccounts();
    const selectedAccount = instance.getActiveAccount() || accounts[0];

    if (!selectedAccount) {
        throw new Error("No active account found");
    }

    const signinAuthority = `${getMSALBaseUrl()}/${getSigninPolicy()}`
    try {
        const tokenResponse = await instance.acquireTokenSilent({
            ...loginRequest,
            authority: signinAuthority,
            account: selectedAccount,
        });
        // Cache the token and its expiration time
        cachedToken = tokenResponse.idToken;

        return cachedToken;
    } catch (error) {
        console.error(error)
        if (error instanceof InteractionRequiredAuthError) {
            console.error("Interaction is required to acquire the token", error);
            try {
                interactionInProgress = true
                const request: RedirectRequest = {
                    authority: signinAuthority,
                    account: selectedAccount,
                    ...loginRequest, // Include the required scopes for your API
                    state: window.location.pathname, // Redirect user back to current page after login
                }
                console.log(request, "login Redirect request - api handler")
                // **Try acquireTokenPopup first to avoid full page reload**
                await instance.loginRedirect(request);
                interactionInProgress = false
            } catch (redirectError) {
                interactionInProgress = false
                console.error("Error during loginRedirect", redirectError);
                throw redirectError;
            }
        } else {
            console.error("Error acquiring token:", error);
            throw error;
        }
    }
};

// Function to decode and check if the token is expired
export const isTokenExpired = (token: string) => {
    try {
        const base64Url = token.split('.')[1]; // Get the payload part of the JWT
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/'); // Fix URL-safe encoding
        const decoded = JSON.parse(atob(base64)); // Decode the base64 URL to JSON
        // The 'exp' claim is in seconds, so convert it to milliseconds
        const expirationTime = decoded.exp * 1000;
        const currentTime = Date.now(); // Get current time in milliseconds 
        const adjustedTime = currentTime + 100000
        return adjustedTime >= expirationTime;

    } catch (error) {
        console.error("Error decoding the token:", error);
        return true; // Assume expired if the decoding fails
    }
};
const fetchImpresontedToken = async () => {
    const impersonateToken = sessionStorage.getItem("impersonateToken")
    if (impersonateToken && !isTokenExpired(impersonateToken)) {
        return impersonateToken;
    }
    const baseUrl = `${getUserApiUrl()}/impersonation/token`
    const accessToken = await handleFetchFcltToken()
    const email = sessionStorage.getItem("impersonateEmail")
    const applicationId = getApplicationId().replace('Inv', "Fclt")
    const defaultHeaders = {
        "Authorization": `Bearer ${accessToken}`,
        "X-Application-Id": applicationId,
        accept: "text/plain",
        "Ocp-Apim-Subscription-Key": getAPIMKey(),
    }
    const response = await axios.post(baseUrl, { email }, { headers: defaultHeaders });
    sessionStorage.setItem("impersonateToken", response?.data?.access_token)
    return response?.data?.access_token
}

export const handleFetchFcltToken = async (): Promise<string> => {
  let token = sessionStorage.getItem("idtokens");
  const tokenRefresh = sessionStorage.getItem("tokenRefresh");

  if (!token || isTokenExpired(token)) {
    const tokenRefreshTime = tokenRefresh ? parseInt(tokenRefresh) + 1 : 1;
    sessionStorage.setItem("tokenRefresh", String(tokenRefreshTime));

    const isLocalhost = window.location.hostname === "localhost";
    const portNumber = window.location.port === "3001" ? 3000 : 3001;
    const baseUrl = isLocalhost
      ? `http://localhost:${portNumber}`
      : getFcltAppUrl();

    const newTokenPromise = new Promise<string>((resolve) => {
      const listener = (event: MessageEvent) => {
        if (event.origin !== baseUrl) return;
        if (typeof event.data === "object" && event.data.status === "tokenRefresh" && event.data.accessToken) {
          sessionStorage.setItem("idtokens", event.data.accessToken);
          window.removeEventListener("message", listener);
          resolve(event.data.accessToken);
        }
      };

      window.addEventListener("message", listener);

      if (window.opener) {
        window.opener.postMessage("tokenRefresh", baseUrl);
      }
    });

    return await newTokenPromise;
  }

  return token;
};


export const handleFetchApi = async ({
    endpoint,
    method = "GET",
    payload,
    baseUrl,
    headers,
    signal,
    keepAlive = false
}: ApiRequestParams): Promise<AxiosResponse> => {

    const instance = getMsalInstance()
    const isImpersonate = sessionStorage.getItem("isImpersonate")
    try {
        const accessToken = isImpersonate === "true" ? await fetchImpresontedToken() : await fetchToken();
        
        const fcltToken =  isImpersonate === "true"? await handleFetchFcltToken() : null;

        const url = baseUrl ? baseUrl : getUserApiUrl();
        // Prepare the default headers
        const defaultHeaders = {
            "Authorization": isImpersonate === "true" ? `Bearer ${fcltToken}` : `Bearer ${accessToken}`,
            "X-Application-Id": getApplicationId(),
            accept: "text/plain",
            "Ocp-Apim-Subscription-Key": getAPIMKey(),
            "Cache-Control": "no-cache",
            keepAlive: keepAlive,
            // If payload is FormData, set Content-Type as multipart/form-data 
            ...(payload instanceof FormData ? { "Content-Type": "multipart/form-data" } : { "Content-Type": "application/json" }), // Automatically choose Content-Type
        };
        if (isImpersonate === "true") {
            defaultHeaders['Impersonate-Token'] = `${accessToken}`;
            defaultHeaders['Is-Impersonate'] = 'true';
        }
        // Merge any custom headers passed in
        const finalHeaders = AxiosHeaders.from({ ...defaultHeaders, ...headers });

        const options: AxiosRequestConfig = {
            method,
            url: `${url}/${endpoint}`,
            headers: finalHeaders,
            data: payload,
            signal,
        };


        const response = await axios.request(options);
        return response;
    } catch (err: any) {
        // If the error is related to token expiration (401), attempt to refresh the token
        if (err?.response?.status === 401 || err?.response?.status === 403) {
            try {
                console.log("Token expired, refreshing...");
                // Clear the cached token on refresh failure
                // alert("You do not have permission to access")
                swalInstance = await Swal.fire({
                    title: isImpersonate === "true" ? 'Session Ended' : "Authorisation Required",
                    text: isImpersonate === "true" ? typeof err?.response?.data === "string" ? err?.response?.data : err?.response?.data?.detail : "You do not have permission to access",
                    icon: "error",
                    confirmButtonText: 'OK',
                    customClass: {
                        confirmButton: 'bg-[#FF5C39]',  // Add a custom class to the button
                        icon: 'text-[10px]'
                    },
                    width: '600px',  // Adjust the popup width
                    padding: '10px',  // Adjust padding inside the popup
                    allowOutsideClick: true,  // Allow closing by clicking outside
                    allowEscapeKey: true,     // Allow closing by pressing the Escape key

                })
                if (swalInstance.isConfirmed) {

                    if (isImpersonate === "true") {
                        window.close()
                    } else {
                        sessionStorage.clear()
                        instance?.logoutRedirect();
                    }
                }

                // await fetchToken(); // Attempt to refresh token
                // return handleFetchApi({ endpoint, method, payload, baseUrl }); 
            } catch (refreshError) {
                console.error("Token refresh failed", refreshError);
            }
        }

        throw err;
    }
};
